import React, { useState, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { Flag } from "lucide-react";
import ExceedsRespIcon from "../../../../icons/exceedsRespIcon.svg";
import Send from "../../../../icons/prime_send.svg?react";
import Received from "../../../../icons/resp_received.svg?react";
import NotSatisfied from "../../../../icons/resp_notStatisfied.svg?react";
import Satisfied from "../../../../icons/res_statisfied.svg?react";
import FollowUp from "../../../../icons/resp_followUp.svg?react";
import "./ResponseTimeline.scss";
import TooltipPortal from "./TooltipPortal";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Represents a single auditee's response data for a question
 */
interface AuditeeResponse {
    auditeeId: number;
    auditeeName: string;
    status: "On Time" | "Overdue";
    receivedOn: string | null;
    respondedOn: string | null;
    askedBy: "AUDITOR" | "AUDITEE" | "SYSTEM";
    satisfied: boolean;
}

/**
 * API Response structure for question analysis
 */
interface ApiResponseData {
    questionId: number;
    questionText: string;
    responseTime: string;
    responseExceeded: boolean;
    responseDeadline: string;
    auditees: AuditeeResponse[];
}

/**
 * Internal event type for timeline visualization
 */
type EventType = "sent" | "received" | "rejected" | "followup" | "satisfied";

/**
 * Internal event structure
 */
interface TimelineEvent {
    day: number;
    type: EventType;
    label: string;
    actualDate: Date;
    isFollowup: boolean;
}

/**
 * Internal auditee structure for rendering
 */
interface TimelineAuditee {
    id: number;
    name: string;
    status: "On Time" | "Overdue";
    deadlineDay: number;
    events: TimelineEvent[];
}

/**
 * Tooltip state
 */
interface TooltipState {
    show: boolean;
    x: number;
    y: number;
    event: TimelineEvent | null;
}

/**
 * Component props
 */
interface ResponseTimelineModalProps {
    show: boolean;
    onClose: () => void;
    apiData?: ApiResponseData | null;
    questionId?: number;
    questionText?: string;
    averageResponseTime?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const EVENT_ICONS: Record<EventType, JSX.Element> = {
    sent: <Send />,
    received: <Received />,
    rejected: <NotSatisfied />,
    satisfied: <Satisfied />,
    followup: <FollowUp />,
};

const EVENT_COLORS: Record<EventType, string> = {
    sent: "#2563eb",
    received: "#5B61CB",
    rejected: "#AE2727",
    satisfied: "#5BCB81",
    followup: "#FF9524",
};

// Spacing between icons when multiple events occur on the same day
const ICON_SPACING_MAP: Record<number, number> = {
    1: 0,
    2: 28,
    3: 26,
    4: 24,
    5: 20,
};

const DEFAULT_ICON_SPACING = 18;

// Event type priority for sorting when timestamps are equal
const EVENT_TYPE_PRIORITY: Record<EventType, number> = {
    sent: 1,
    received: 2,
    rejected: 3,
    satisfied: 3,
    followup: 4,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalizes a date to midnight for accurate day comparison
 */
const normalizeDate = (date: Date): Date => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};

/**
 * Formats a date for tooltip display
 */
const formatDate = (date: Date): string => {
    return date.toLocaleString("en-US", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
};

/**
 * Gets icon spacing based on number of events on the same day
 */
const getIconSpacing = (count: number): number => {
    return ICON_SPACING_MAP[count] ?? DEFAULT_ICON_SPACING;
};

/**
 * Calculates day number from start date
 */
const calculateDayNumber = (date: Date, startDate: Date): number => {
    const normalizedDate = normalizeDate(date);
    const normalizedStart = normalizeDate(startDate);
    const diffMs = normalizedDate.getTime() - normalizedStart.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
};

// ============================================================================
// DATA TRANSFORMATION
// ============================================================================

/**
 * Transforms API response data into internal timeline structure
 */
const transformApiData = (data: ApiResponseData | null | undefined) => {
    // Handle empty or invalid data
    if (!data || !data.auditees || data.auditees.length === 0) {
        return {
            auditees: [],
            totalDays: 31,
            monthName: "No Data",
            overdueCount: 0,
            startDate: new Date(),
        };
    }

    const { auditees: apiAuditees, responseDeadline } = data;

    // Parse the deadline date
    const deadlineDate = responseDeadline ? normalizeDate(new Date(responseDeadline)) : null;

    // Collect all dates to determine timeline range
    const allDates: Date[] = [];

    if (deadlineDate) {
        allDates.push(deadlineDate);
    }

    apiAuditees.forEach((auditee) => {
        if (auditee.receivedOn) {
            allDates.push(new Date(auditee.receivedOn));
        }
        if (auditee.respondedOn) {
            allDates.push(new Date(auditee.respondedOn));
        }
    });

    // If no dates available, return empty state
    if (allDates.length === 0) {
        return {
            auditees: [],
            totalDays: 31,
            monthName: "No Data",
            overdueCount: 0,
            startDate: new Date(),
        };
    }

    // Calculate timeline range
    const minDate = normalizeDate(new Date(Math.min(...allDates.map(d => d.getTime()))));
    const maxDate = normalizeDate(new Date(Math.max(...allDates.map(d => d.getTime()))));

    // Ensure deadline is included in range if it exists
    const endDate = deadlineDate && deadlineDate.getTime() > maxDate.getTime()
        ? deadlineDate
        : maxDate;

    const totalDays = Math.ceil((endDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    // Generate month label
    const startMonth = minDate.toLocaleString("en-US", { month: "short" });
    const endMonth = endDate.toLocaleString("en-US", { month: "short" });
    const startYear = minDate.getFullYear();
    const endYear = endDate.getFullYear();

    const monthName =
        startMonth === endMonth && startYear === endYear
            ? `${startMonth} ${startYear}`
            : `${startMonth} ${startYear} - ${endMonth} ${endYear}`;

    // Group and sort auditees by auditeeId, then sort by receivedOn to process in chronological order
    const sortedAuditees = [...apiAuditees].sort((a, b) => {
        if (a.auditeeId !== b.auditeeId) {
            return a.auditeeId - b.auditeeId;
        }
        // Sort by receivedOn time for same auditee
        const timeA = a.receivedOn ? new Date(a.receivedOn).getTime() : 0;
        const timeB = b.receivedOn ? new Date(b.receivedOn).getTime() : 0;
        return timeA - timeB;
    });

    // Group auditees by auditeeId to merge multiple entries for the same auditee
    const auditeeMap = new Map<number, {
        name: string;
        status: "On Time" | "Overdue";
        events: TimelineEvent[];
        hasMainQuestion: boolean; // Track if main question has been processed
    }>();

    sortedAuditees.forEach((auditee) => {
        // Get or create auditee entry
        let auditeeEntry = auditeeMap.get(auditee.auditeeId);
        if (!auditeeEntry) {
            auditeeEntry = {
                name: auditee.auditeeName,
                status: auditee.status,
                events: [],
                hasMainQuestion: false,
            };
            auditeeMap.set(auditee.auditeeId, auditeeEntry);
        }

        // Update status to "Overdue" if any instance is overdue
        if (auditee.status === "Overdue") {
            auditeeEntry.status = "Overdue";
        }

        // Determine if this is a follow-up based on:
        // 1. askedBy is not AUDITOR, OR
        // 2. This auditee already has a main question processed
        const isFollowup = auditee.askedBy !== "AUDITOR" || auditeeEntry.hasMainQuestion;

        // Mark that main question has been seen
        if (auditee.askedBy === "AUDITOR") {
            auditeeEntry.hasMainQuestion = true;
        }

        // Add "Question Sent" or "Follow-up Requested" event
        if (auditee.receivedOn) {
            const receivedDate = new Date(auditee.receivedOn);
            auditeeEntry.events.push({
                day: calculateDayNumber(receivedDate, minDate),
                type: isFollowup ? "followup" : "sent",
                label: isFollowup ? "Follow-up Requested" : "Question Sent",
                actualDate: receivedDate,
                isFollowup,
            });
        }

        // Add "Response Received" or "Follow-up Responded" event
        if (auditee.respondedOn) {
            const respondedDate = new Date(auditee.respondedOn);
            auditeeEntry.events.push({
                day: calculateDayNumber(respondedDate, minDate),
                type: "received",
                label: isFollowup ? "Follow-up Responded" : "Response Received",
                actualDate: respondedDate,
                isFollowup,
            });

            // Add satisfaction status event
            if (auditee.satisfied) {
                auditeeEntry.events.push({
                    day: calculateDayNumber(respondedDate, minDate),
                    type: "satisfied",
                    label: "Response Satisfied",
                    actualDate: respondedDate,
                    isFollowup,
                });
            } else {
                auditeeEntry.events.push({
                    day: calculateDayNumber(respondedDate, minDate),
                    type: "rejected",
                    label: "Not Satisfied",
                    actualDate: respondedDate,
                    isFollowup,
                });
            }
        }
    });

    // Convert map to array and sort events chronologically
    const transformedAuditees: TimelineAuditee[] = [];
    let overdueCount = 0;

    auditeeMap.forEach((auditeeEntry, auditeeId) => {
        // Sort events by actual date, then by type priority when timestamps are equal
        auditeeEntry.events.sort((a, b) => {
            const timeDiff = a.actualDate.getTime() - b.actualDate.getTime();
            if (timeDiff !== 0) {
                return timeDiff;
            }
            // If timestamps are equal, sort by event type priority
            return EVENT_TYPE_PRIORITY[a.type] - EVENT_TYPE_PRIORITY[b.type];
        });

        // Count overdue auditees
        if (auditeeEntry.status === "Overdue") {
            overdueCount++;
        }

        // Calculate deadline day
        const deadlineDay = deadlineDate ? calculateDayNumber(deadlineDate, minDate) : 0;

        transformedAuditees.push({
            id: auditeeId,
            name: auditeeEntry.name,
            status: auditeeEntry.status,
            deadlineDay,
            events: auditeeEntry.events,
        });
    });

    return {
        auditees: transformedAuditees,
        totalDays,
        monthName,
        overdueCount,
        startDate: minDate,
    };
};

// ============================================================================
// COMPONENT
// ============================================================================

const ResponseTimelineModal: React.FC<ResponseTimelineModalProps> = ({
    show,
    onClose,
    apiData,
    questionId = 1,
    questionText = "",
    averageResponseTime = "0h",
}) => {
    // Tooltip state
    const [tooltip, setTooltip] = useState<TooltipState>({
        show: false,
        x: 0,
        y: 0,
        event: null,
    });

    // Transform data using useMemo for performance
    const { auditees, totalDays, monthName, overdueCount, startDate } = useMemo(
        () => transformApiData(apiData),
        [apiData]
    );

    // Generate day labels
    const days = useMemo(() => {
        return Array.from({ length: totalDays }, (_, i) => {
            const date = new Date(startDate);
            date.setDate(date.getDate() + i);
            return {
                dayNumber: i + 1,
                date,
                label: `${date.toLocaleString("en-US", { month: "short" })} ${date.getDate()}`,
            };
        });
    }, [totalDays, startDate]);

    // Event handlers
    const handleMouseEnter = (event: TimelineEvent, rect: DOMRect) => {
        setTooltip({
            show: true,
            x: rect.left + rect.width / 2,
            y: rect.top - 10,
            event,
        });
    };

    const handleMouseLeave = () => {
        setTooltip({
            show: false,
            x: 0,
            y: 0,
            event: null,
        });
    };

    // Render functions
    const getIcon = (type: EventType): JSX.Element | null => {
        return EVENT_ICONS[type] || null;
    };

    const getLabelColor = (type: EventType): string => {
        return EVENT_COLORS[type] || "#000";
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            dialogClassName="modal-95w"
            className="response-timeline-modal"
            backdrop="static"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="w-100 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-bold text-dark timeline-heading">
                        Q{questionId} Response Timeline
                    </h6>
                    <span className="text-muted fw-semibold">
                        Response Time: {averageResponseTime}
                    </span>
                </div>
            </Modal.Header>

            <div className="px-4 pb-3 d-flex justify-content-between align-items-start">
                <p className="mb-0 text-muted small question-text">
                    Question: {questionText}
                </p>
                {overdueCount > 0 && (
                    <span className="text-danger small d-flex align-items-center gap-1 text-overdue">
                        <img src={ExceedsRespIcon} alt="exceeds" height={14} />
                        {overdueCount} Auditee{overdueCount > 1 ? "s" : ""} Exceed Response Time
                    </span>
                )}
            </div>

            <Modal.Body className="p-4 pt-0">
                <div className="timeline-container">
                    {/* Header */}
                    <div className="timeline-header">
                        <div className="auditee-headers">Auditee</div>
                        <div className="month-header">
                            <div className="month-title">
                                {monthName} ({totalDays} Days)
                            </div>
                            <div className="days-grid" style={{ "--totalDays": totalDays } as any}>
                                {days.map((d) => (
                                    <div key={d.dayNumber} className="day-label">
                                        {d.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Rows */}
                    <div className="custom_scroll timeline-body">
                        {auditees.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                No timeline data available
                            </div>
                        ) : (
                            auditees.map((auditee) => {
                                const isOverdue = auditee.status === "Overdue";
                                const lastEventDay = auditee.events[auditee.events.length - 1]?.day || 0;

                                // Group events by day (preserving original order)
                                const groupedEvents: Record<number, TimelineEvent[]> = auditee.events.reduce(
                                    (acc, event) => {
                                        if (!acc[event.day]) {
                                            acc[event.day] = [];
                                        }
                                        acc[event.day].push(event);
                                        return acc;
                                    },
                                    {} as Record<number, TimelineEvent[]>
                                );

                                // Timeline line calculations
                                const eventsBeforeDeadline = auditee.events.filter(
                                    (e) => e.day <= auditee.deadlineDay
                                );
                                const lastBeforeDeadline =
                                    eventsBeforeDeadline.length > 0
                                        ? Math.max(...eventsBeforeDeadline.map((e) => e.day))
                                        : 0;

                                // Blue line calculation: extends to last event position (center of day)
                                // For overdue: extends only to deadline
                                // For on-time: extends to last event
                                const blueLineEndDay = isOverdue
                                    ? Math.min(lastBeforeDeadline || lastEventDay, auditee.deadlineDay)
                                    : lastEventDay;

                                // Calculate percentage to center of the day where last event is
                                const blueLineWidth = ((blueLineEndDay - 0.5) / totalDays) * 100;

                                return (
                                    <div key={auditee.id} className="timeline-row">
                                        <div className="auditee-cell">
                                            <div className="auditee-name">{auditee.name}</div>
                                            <span className={`status-badge ${isOverdue ? "overdue" : "ontime"}`}>
                                                {auditee.status}
                                            </span>
                                        </div>

                                        <div className="track-cell">
                                            {/* Blue solid line - extends to last event (or deadline if overdue) */}
                                            <div
                                                className="timeline-line blue-line"
                                                style={{
                                                    width: `${blueLineWidth}%`,
                                                }}
                                            />

                                            {/* Red dashed line from deadline to end (if overdue) */}
                                            {isOverdue && lastEventDay > auditee.deadlineDay && (
                                                <div
                                                    className="timeline-line red-line"
                                                    style={{
                                                        width: `${((lastEventDay - auditee.deadlineDay) / totalDays) * 100}%`,
                                                        left: `${(auditee.deadlineDay / totalDays) * 100}%`,
                                                    }}
                                                />
                                            )}

                                            {/* Curved extension for overdue */}
                                            {isOverdue && lastBeforeDeadline < lastEventDay && (
                                                <div className="overdue-curve">
                                                    <svg width="100%" height="40" className="curve-svg">
                                                        <path
                                                            d={`M ${(lastBeforeDeadline / totalDays) * 100}% 20 Q 80% 50, ${(lastEventDay / totalDays) * 100}% 35`}
                                                            stroke="#ef4444"
                                                            strokeWidth="2"
                                                            strokeDasharray="6,4"
                                                            fill="none"
                                                        />
                                                    </svg>
                                                </div>
                                            )}

                                            {/* Event Points */}
                                            {Object.entries(groupedEvents)
                                                .sort((a, b) => Number(a[0]) - Number(b[0]))
                                                .map(([dayStr, eventsOnDay]) => {
                                                    const day = parseInt(dayStr);
                                                    const count = eventsOnDay.length;
                                                    const basePercent = ((day - 0.5) / totalDays) * 100;
                                                    const spacing = getIconSpacing(count);

                                                    return eventsOnDay.map((event, index) => {
                                                        const offsetFromCenter = (index - (count - 1) / 2) * spacing;
                                                        const safeOffset =
                                                            day <= 3
                                                                ? Math.max(offsetFromCenter, -10)
                                                                : offsetFromCenter;

                                                        return (
                                                            <div
                                                                key={`${day}-${index}`}
                                                                className="event-point"
                                                                style={{
                                                                    left: `calc(${basePercent}% + ${safeOffset}px)`,
                                                                    top: "50%",
                                                                    transform: "translateY(-50%)",
                                                                    zIndex: 10,
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    handleMouseEnter(event, rect);
                                                                }}
                                                                onMouseLeave={handleMouseLeave}
                                                            >
                                                                <div
                                                                    className="event-icon"
                                                                    style={{ color: getLabelColor(event.type) }}
                                                                >
                                                                    {getIcon(event.type)}
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })}

                                            {/* Deadline Flag */}
                                            {auditee.deadlineDay > 0 && (
                                                <div
                                                    className="deadline-flag"
                                                    style={{
                                                        left: `calc(${((auditee.deadlineDay - 0.5) / totalDays) * 100}%)`,
                                                    }}
                                                >
                                                    <Flag size={20} color="#dc2626" fill="#dc2626" />
                                                    <div className="flag-line" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Tooltip */}
                {tooltip.show && tooltip.event && (
                    <TooltipPortal>
                        <div
                            className="custom-tooltip-container tooltip-card"
                            style={{
                                position: "fixed",
                                left: tooltip.x,
                                top: tooltip.y,
                                transform: "translate(-50%, -100%)",
                                zIndex: 9999999,
                                border: `1px solid ${getLabelColor(tooltip.event.type)}`,
                            }}
                        >
                            <div
                                className="tooltip-title"
                                style={{ color: getLabelColor(tooltip.event.type) }}
                            >
                                {tooltip.event.label}
                            </div>
                            <div className="tooltip-date">{formatDate(tooltip.event.actualDate)}</div>
                            <div style={{ borderBottom: "1px solid #e0e7ff" }} />
                        </div>
                    </TooltipPortal>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ResponseTimelineModal;
